import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { type PayoutBatchWarning, PayoutBatchWarningCode } from "../../types/payoutBatches.types";
import { batchPath } from "../../utils/batchLinks";
import { BatchFeesUpdatedAlert } from "./BatchFeesUpdatedAlert";

export interface BatchWarningsBannerProps {
  warnings: PayoutBatchWarning[];
}

/**
 * Batch-level warnings — advisory, never blocking. A repricing after a fee change gets its own alert with the
 * before/after amounts; the rest (same file uploaded before, ignored columns) share one list.
 */
export function BatchWarningsBanner({ warnings }: BatchWarningsBannerProps) {
  const { t } = useTranslations("payoutBatches");
  const feesUpdated = warnings.find((warning) => warning.code === PayoutBatchWarningCode.FEES_UPDATED);
  const others = warnings.filter((warning) => warning.code !== PayoutBatchWarningCode.FEES_UPDATED);
  if (!feesUpdated && others.length === 0) return null;

  return (
    <div className="space-y-3">
      {feesUpdated && <BatchFeesUpdatedAlert warning={feesUpdated} />}
      {others.length > 0 && (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          text={
            <ul className="space-y-1">
              {others.map((warning, index) => (
                <li key={`${warning.code}-${index}`} className="flex flex-wrap items-center gap-x-2">
                  <span>{warning.message}</span>
                  {warning.code === PayoutBatchWarningCode.SAME_FILE_UPLOADED_BEFORE && warning.batch_id && (
                    <Link
                      to={batchPath(warning.batch_id)}
                      className="font-medium underline underline-offset-2"
                    >
                      {t("warning_same_file_link")}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  );
}
